import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';

import { z } from 'zod';

import { MusicRegistry } from '../registries/MusicRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { Tool } from './Tool';

import { AudioSearchSourceEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';

const RESULT_COUNT = 10;

export const musicSearchSchema = z.object({
    query: z
        .string()
        .optional()
        .describe(
            'The search query to find relevant music. Works best combining genre, energy, and instrumentation that fit the video\'s mood (e.g. "playful ukulele light", "cinematic strings emotional build", "punchy electronic upbeat drums"); only use "corporate" for B2B videos. Must be in English. Required when source is "library". Optional when source is "uploads" — omit it to list the user\'s most recent uploaded music, or provide a query to filter by name.',
        ),
    source: z
        .enum(['library', 'uploads'])
        .default('library')
        .describe(
            'Where to search. "library" (default) searches the stock music library. "uploads" searches only the music the user has previously uploaded to their account. Use "uploads" only when the user explicitly references their own uploaded music.',
        ),
});
export type MusicSearchArgs = z.infer<typeof musicSearchSchema>;

interface MusicRepresentation {
    id: string;
    type: string;
    title: string;
    tags: string;
    duration: number;
}

export class MusicSearchTool extends Tool<typeof musicSearchSchema> {
    protected override readonly schema = musicSearchSchema;
    protected override readonly toolName = TOOL_NAMES.searchMusic;
    protected override readonly toolDescription =
        'Search for music assets using a text query. By default this searches the stock music library. Set source to "uploads" only when the user references their own uploaded music.';

    public override customRenderer = MusicSearchResultRenderer;

    public override getToolDisplayName(): string {
        return 'Searching for music';
    }

    public get statusIcon(): string {
        return 'library_music';
    }

    public async execute(args: MusicSearchArgs, context: ToolExecutionContext) {
        const { source = 'library' } = args;
        const query = args.query?.trim() ?? '';

        if (source !== 'uploads' && !query) {
            return 'Error: a search query is required when searching the music library. Provide a descriptive query (mood, genre, style), or set source to "uploads" to browse the user\'s uploaded music.';
        }

        try {
            const response = await ApiService.audioRetrieve({
                query: {
                    q: query,
                    search_source:
                        source === 'uploads'
                            ? AudioSearchSourceEnum.UPLOADS
                            : AudioSearchSourceEnum.CC,
                    page: 1,
                    count: RESULT_COUNT,
                    autocorrect: false,
                    did_you_mean: false,
                    filter_by_user_level: false,
                },
            });

            // Add results to MusicRegistry and return representations
            const musicRegistry = MusicRegistry.getInstance();
            const representations = response.data.results
                .filter((result) => result.audio) // Only process results with audio
                .map((result) => {
                    const id = musicRegistry.addItem(result);
                    return musicRegistry.getRepresentation(id);
                });

            if (representations.length === 0) {
                if (source === 'uploads') {
                    return query
                        ? `No uploaded music found matching "${query}". The user may not have uploaded music with that name — try omitting the query to list all their uploaded music.`
                        : `The user has no uploaded music.`;
                }
                return `No music found for "${query}". Try different search terms like genre (jazz, rock, classical), mood (upbeat, calm, dramatic), or instruments (piano, guitar, orchestral).`;
            }

            return JSON.stringify(representations);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error searching for music: ${errorMessage}`;
        }
    }
}

const MusicSearchResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    let parsedResult;
    try {
        parsedResult = JSON.parse(result);
    } catch (error) {
        return <div>{result}</div>;
    }

    const representations = parsedResult || [];
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const query = args.query?.trim() || '';
    const musicRegistry = MusicRegistry.getInstance();

    return (
        <div>
            Found {representations.length} music track{representations.length !== 1 ? 's' : ''}
            {query ? ` for "${query}"` : ''}
            <div>
                {representations.slice(0, 6).map((rep: MusicRepresentation, index: number) => {
                    const audioSourceData = musicRegistry.getAudioSourceData(rep.id);
                    const mp3Url = audioSourceData?.mp3_url;

                    return (
                        <div key={index} className="border p-l5-8 mb-l5-8 rounded">
                            <div className="fw-bold">{rep.title}</div>
                            <div className="text-muted small">
                                Duration: {Math.floor(rep.duration / 60)}:
                                {String(Math.floor(rep.duration % 60)).padStart(2, '0')}
                            </div>
                            {rep.tags && <div className="text-muted small">Tags: {rep.tags}</div>}
                            {mp3Url && (
                                <div>
                                    <audio
                                        controls
                                        preload="none"
                                        className="w-100"
                                        style={{ maxWidth: '300px', height: '30px' }}
                                    >
                                        <source src={mp3Url} type="audio/mpeg" />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {representations.length > 6 && (
                <div>... and {representations.length - 6} more tracks</div>
            )}
        </div>
    );
};
